import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import { APP_NAMESPACE } from '@/meta'

/**
 * Client HTTP unique de l'application.
 *
 * Toutes les fonctions de `src/api/*` passent par ici — aucune vue n'appelle
 * `axios` ni `fetch` directement. Trois choses vivent à cet endroit et nulle
 * part ailleurs :
 *
 *   1. la session persistée (jeton d'accès + jeton de rafraîchissement) ;
 *   2. le rafraîchissement automatique sur 401, sérialisé (voir plus bas) ;
 *   3. la normalisation des erreurs en `ApiError`, pour que les vues n'aient
 *      jamais à connaître la forme d'une erreur axios.
 */

// -----------------------------------------------------------------------------
// Session persistée
// -----------------------------------------------------------------------------

/** Forme de l'utilisateur renvoyée par `POST /auth/login` et `GET /auth/me`. */
export interface SessionUser {
  id: string
  email: string
  fullName: string
  role: 'owner' | 'admin' | 'member' | 'viewer'
  organisationId: string
  /** Nom de l'organisation, affiché par la barre latérale sur chaque écran. */
  organisationName: string
  locale: string
  /**
   * L'organisation de l'utilisateur est-elle l'organisation de GESTION ?
   *
   * Sert à AFFICHER la bascule de périmètre. Ce n'est pas une autorisation : le
   * serveur revérifie le droit sur chaque route du périmètre plateforme (voir
   * `core/superuser/routes.ts`). Une valeur bricolée dans le stockage local ne
   * donne donc accès à rien — elle fait juste apparaître un menu qui répondra
   * 403.
   */
  isHyperadmin: boolean
  /**
   * « Jump » backoffice en cours (E15-1) : la session travaille dans
   * l'organisation d'un CLIENT — `organisationId` et `organisationName` sont
   * alors les siennes — depuis l'organisation de gestion `originOrganisationId`.
   * `null` = session ordinaire. Optionnel : une session persistée AVANT ce champ
   * n'en a pas, et `refreshUser()` la complète au premier `/auth/me`.
   *
   * Deux usages, tous deux non négociables : un bandeau PERMANENT (on ne doit
   * jamais oublier qu'on agit chez un client) et le retour, qui n'est qu'une
   * fermeture côté serveur.
   */
  jump?: JumpSession | null
}

export interface JumpSession {
  originOrganisationId: string
  originOrganisationName: string
  /** Fin du jump, en ISO. Affichée : un bandeau sans échéance laisse croire
   *  que l'accès dure, alors que le serveur le ferme en silence. */
  expiresAt: string
}

export interface Session {
  accessToken: string
  refreshToken: string
  user: SessionUser
}

// Namespace pointé : toutes les clés de stockage du projet commencent par
// `APP_NAMESPACE.` (voir src/meta.ts). Sans convention, on retrouve six clés
// orphelines en deux ans, et on n'ose plus en supprimer aucune.
const STORAGE_KEY = `${APP_NAMESPACE}.session`

/**
 * Lit la session persistée. Retourne `null` sur toute anomalie (stockage
 * indisponible, JSON corrompu, forme inattendue) : une session illisible doit
 * déconnecter proprement, jamais faire planter le démarrage de l'application.
 */
export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Session>
    if (!parsed?.accessToken || !parsed?.refreshToken || !parsed?.user) return null
    return parsed as Session
  } catch {
    return null
  }
}

export function saveSession(session: Session): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch {
    // Navigation privée ou quota plein : la session reste en mémoire pour cet
    // onglet. Dégradation acceptable — on ne bloque pas la connexion.
  }
  current = session
}

/**
 * Remplace l'UTILISATEUR de la session persistée, en gardant les jetons.
 *
 * Nécessaire parce que deux lecteurs coexistent : le store Pinia (hydraté au
 * montage) et le garde du routeur, qui lit la session persistée — il s'exécute
 * avant le premier montage de composant, quand le store n'existe pas encore.
 *
 * Sans cette synchronisation, les deux divergent dès qu'un champ apparaît dans
 * la charge de `/auth/me` : le store voit le nouveau champ, la session non, et
 * l'interface affiche un item de menu que le garde refuse. Constaté exactement
 * ainsi avec `isHyperadmin` — le clic sur « Organisations » ne faisait « rien »,
 * parce que le garde redirigeait silencieusement vers l'écran déjà affiché.
 */
export function updateSessionUser(user: SessionUser): void {
  if (current === null) return
  saveSession({ ...current, user })
}

export function clearSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Sans importance : `current = null` suffit à couper l'accès.
  }
  current = null
}

// Copie en mémoire, pour ne pas relire (et re-parser) le localStorage à chaque
// requête. `loadSession()` au démarrage rétablit la session d'un rechargement.
let current: Session | null = loadSession()

export function currentSession(): Session | null {
  return current
}

// -----------------------------------------------------------------------------
// Erreurs
// -----------------------------------------------------------------------------

/**
 * Erreur normalisée. Les vues attrapent `ApiError` et affichent `.message` :
 * elles n'ont jamais à distinguer un échec réseau d'une réponse 500.
 */
export class ApiError extends Error {
  /** Statut HTTP, ou 0 quand la requête n'a jamais atteint le serveur. */
  readonly status: number
  /** Code métier optionnel renvoyé par l'API (ex. `invalid_credentials`). */
  readonly code: string | undefined
  /** Détails de validation, indexés par champ, tels que renvoyés par l'API. */
  readonly fields: Record<string, string> | undefined

  constructor(
    message: string,
    status: number,
    code?: string,
    fields?: Record<string, string>,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.fields = fields
  }

  /** Vrai quand le serveur n'a pas répondu (hors ligne, port fermé, DNS). */
  get isNetwork(): boolean {
    return this.status === 0
  }
}

/**
 * Traduit une erreur axios en `ApiError`, avec un message affichable tel quel.
 *
 * ASYNCHRONE pour une seule raison : le corps d'erreur d'un TÉLÉCHARGEMENT
 * (`responseType: 'blob'` — preuve, version scellée, E8-3) arrive en `Blob`,
 * pas en objet. Sans le relire, le `code` typé (`preuve-en-preparation`, E7-2)
 * serait perdu à la frontière et la vue ne pourrait plus distinguer « le
 * dossier se produit, réessayez » d'un vrai incident — exactement le cas où le
 * message le plus rassurant disparaîtrait. Un `Blob` se relit sans être
 * consommé (`text()` est répétable), et une lecture qui échoue retombe sur le
 * message générique du statut, jamais sur un message inventé.
 */
async function toApiError(error: unknown): Promise<ApiError> {
  if (error instanceof ApiError) return error

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError
    const status = axiosError.response?.status ?? 0
    const brut = axiosError.response?.data

    let body:
      | {
          error?: string
          message?: string
          code?: string
          fields?: Record<string, string>
        }
      | undefined

    if (brut instanceof Blob) {
      try {
        body = JSON.parse(await brut.text()) as typeof body
      } catch {
        body = undefined
      }
    } else if (brut !== undefined && brut !== null && typeof brut === 'object') {
      body = brut as typeof body
    }

    if (status === 0) {
      return new ApiError(
        'Serveur injoignable. Vérifiez que l’API est démarrée.',
        0,
        'network_error',
      )
    }
    const message =
      body?.error ??
      body?.message ??
      `La requête a échoué (HTTP ${status}).`
    return new ApiError(message, status, body?.code, body?.fields)
  }

  return new ApiError(
    error instanceof Error ? error.message : 'Erreur inattendue.',
    0,
    'unknown',
  )
}

// -----------------------------------------------------------------------------
// Instance axios
// -----------------------------------------------------------------------------

const baseURL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://127.0.0.1:3000'

/**
 * Adresse publique de l'API, normalisée, suffixe `/api` compris.
 *
 * Exposée pour la documentation d'intégration (`components/docs/`) : les
 * exemples copiés-collés doivent montrer le domaine RÉEL que le SPA appelle,
 * pas `window.location.origin` — en développement, l'API et le SPA ne vivent
 * pas sur la même origine.
 */
export const API_BASE_URL = `${baseURL.replace(/\/+$/, '')}/api`

export const http = axios.create({
  baseURL: `${baseURL.replace(/\/+$/, '')}/api`,
  timeout: 20_000,
  // PAS de `Content-Type` par défaut ici. Axios le pose lui-même dès qu'il y a
  // un corps à sérialiser, et l'annoncer sur TOUTE requête casse celles qui
  // n'en ont pas : un `DELETE` sans corps arrivait avec
  // `Content-Type: application/json` et Fastify le rejetait en 400
  // (FST_ERR_CTP_EMPTY_JSON_BODY). Autrement dit, aucune suppression ne
  // fonctionnait — vérifié, puis corrigé ici et rendu tolérable côté serveur.
})

/** L'en-tête d'autorisation déjà présent, quelle que soit sa casse. */
function lireAutorisation(config: InternalAxiosRequestConfig): unknown {
  const entetes = config.headers as unknown as {
    get?: (nom: string) => unknown
  } & Record<string, unknown>
  if (typeof entetes.get === 'function') return entetes.get('Authorization') ?? undefined
  return entetes['Authorization'] ?? entetes['authorization'] ?? undefined
}

/** Marque une requête déjà rejouée après rafraîchissement (anti-boucle). */
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean
  /** L'appelant a fourni SON jeton : ni écrasé à l'aller, ni rejoué avec le nôtre. */
  _jetonPropre?: boolean
}

http.interceptors.request.use((config) => {
  /*
   * UN `Authorization` POSÉ PAR L'APPELANT FAIT AUTORITÉ.
   *
   * Cet intercepteur l'ÉCRASAIT. L'entrée par preuve beTool pose le sien —
   * c'est tout son objet : présenter un jeton d'identité pour OBTENIR une
   * session de ce service. Quand une session locale existait déjà, la preuve
   * était remplacée par l'ancien jeton juste avant l'envoi, et le serveur
   * refusait en 401.
   *
   * Le symptôme n'accusait pas le client : on revenait à la liste des comptes,
   * en boucle, et tout le reste de la chaîne répondait 200. Le défaut ne se
   * produisait QUE si une session survivait — invisible en ligne de commande,
   * invisible sur un navigateur neuf. Mesuré en production.
   */
  /* La casse de l'en-tête N'EST PAS garantie : l'entrée par preuve pose
     « authorization » en minuscules. Lire la seule forme capitalisée laissait
     passer le cas même qu'on corrige — attrapé par le test, pas par la
     relecture. `AxiosHeaders.get` est insensible à la casse ; l'objet nu, non. */
  if (lireAutorisation(config) !== undefined) {
    ;(config as RetriableConfig)._jetonPropre = true
    return config
  }
  if (current?.accessToken) {
    config.headers.Authorization = `Bearer ${current.accessToken}`
  }
  return config
})

// Rafraîchissement SÉRIALISÉ. Sans cette promesse partagée, un écran qui lance
// cinq requêtes en parallèle au chargement déclenche cinq rafraîchissements
// concurrents : le backend révoque le jeton à chaque rotation, donc quatre des
// cinq échouent et déconnectent l'utilisateur. Ici, le premier 401 crée la
// promesse, les suivants l'attendent.
let refreshing: Promise<string> | null = null

/**
 * Rafraîchit le jeton d'accès, en PARTAGEANT la tentative en cours.
 *
 * Exportée parce que le client temps réel en a besoin : quand le serveur ferme
 * le canal pour jeton expiré, il faut un jeton neuf avant de se reconnecter. S'il
 * appelait son propre rafraîchissement, deux rotations concurrentes partiraient
 * — et comme le serveur révoque l'ancien jeton à chaque rotation, l'une des deux
 * échouerait et déconnecterait l'utilisateur. C'est exactement le défaut que la
 * promesse partagée évite côté HTTP ; il n'y a aucune raison d'en ouvrir une
 * seconde porte.
 */
export function refreshSessionOnce(): Promise<string> {
  refreshing ??= refreshAccessToken().finally(() => {
    refreshing = null
  })
  return refreshing
}

async function refreshAccessToken(): Promise<string> {
  const session = current
  if (!session) throw new ApiError('Session absente.', 401, 'no_session')

  // Instance NUE (pas `http`) : passer par l'instance intercepteur ferait
  // repasser la réponse dans la logique de 401 et créerait une récursion.
  const response = await axios.post<{ accessToken: string; refreshToken: string }>(
    `${baseURL.replace(/\/+$/, '')}/api/auth/refresh`,
    { refreshToken: session.refreshToken },
    { timeout: 10_000, headers: { 'Content-Type': 'application/json' } },
  )

  const next: Session = {
    ...session,
    accessToken: response.data.accessToken,
    // Le backend fait tourner le jeton de rafraîchissement à chaque usage
    // (rotation) : réutiliser l'ancien serait rejeté au coup suivant.
    refreshToken: response.data.refreshToken,
  }
  saveSession(next)
  return next.accessToken
}

// Rappel posé par main.ts après l'installation de Pinia : le client HTTP ne
// connaît ni le store ni le routeur (il serait alors impossible de le tester
// sans monter l'application entière).
let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler
}

/**
 * Code d'erreur de la coupure d'accès signature (E9-7), émis par DEUX surfaces
 * du backend — la vérification de clé v1 et le hook de la console
 * (`core/org/acces-signature.ts`). Le frontend branche dessus, pas sur le
 * message : un libellé évolue, un code typé est un contrat.
 */
export const CODE_ACCES_RETIRE = 'signature-non-activee'

/**
 * Rappel posé par main.ts, même doctrine que `setUnauthorizedHandler` : le
 * client signale « l'accès signature est coupé », le câblage décide de
 * l'écran. Poser la navigation ici importerait le routeur dans le module le
 * plus testé du projet — et l'erreur DOIT quand même rejeter : l'écran courant
 * doit apprendre que sa requête a échoué, pas croire à un succès silencieux.
 * `null` réarme le défaut (tests).
 */
let onAccesRetire: (() => void) | null = null

export function setAccesRetireHandler(handler: (() => void) | null): void {
  onAccesRetire = handler
}

http.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) {
      const normalisee = await toApiError(error)
      // Coupure d'accès signature (E9-7) : ce 403 n'est PAS une erreur de
      // formulaire — l'écran courant ne peut plus rien afficher d'utile. Le
      // signal part AVANT le rejet, et le rejet a lieu quand même : la vue
      // appelante doit savoir que sa requête a échoué.
      if (normalisee.status === 403 && normalisee.code === CODE_ACCES_RETIRE) {
        onAccesRetire?.()
      }
      return Promise.reject(normalisee)
    }

    const config = error.config as RetriableConfig | undefined
    const isRefreshCall = config?.url?.includes('/auth/refresh')

    // Pas de session, requête déjà rejouée, ou c'est le rafraîchissement
    // lui-même qui a échoué : plus rien à tenter, on déconnecte.
    if (!config || config._retried || isRefreshCall || !current) {
      clearSession()
      onUnauthorized?.()
      return Promise.reject(await toApiError(error))
    }

    try {
      // Même fonction que celle utilisée par le client temps réel : une seule
      // implémentation, donc une seule promesse partagée.
      const token = await refreshSessionOnce()
      config._retried = true
      /*
       * Même règle qu'à l'aller : une requête qui porte SON propre jeton n'est
       * pas rejouée avec celui de la session. Rafraîchir n'y changerait rien —
       * ce n'est pas notre session qui a expiré, c'est le jeton présenté qui a
       * été refusé — et le rejeu masquait la vraie cause derrière un second 401.
       */
      if (config._jetonPropre !== true) config.headers.Authorization = `Bearer ${token}`
      return http.request(config)
    } catch {
      clearSession()
      onUnauthorized?.()
      return Promise.reject(await toApiError(error))
    }
  },
)

// -----------------------------------------------------------------------------
// Enveloppes typées
// -----------------------------------------------------------------------------

// `http` renvoie une réponse axios ; ces cinq helpers renvoient directement le
// corps typé. Cela évite un `.data` — et un oubli de `.data` — à chaque appel
// dans les modules de src/api.
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  try {
    const { data } = await http.get<T>(url, config)
    return data
  } catch (erreur) {
    if (!doitRejouerGet(erreur, config)) throw erreur
    // Petite pause avant la reprise : un échec réseau qui se rejoue dans la
    // même milliseconde frappe un serveur qui n'a pas eu le temps de récupérer.
    await new Promise((resoudre) => setTimeout(resoudre, 400))
    const { data } = await http.get<T>(url, config)
    return data
  }
}

/**
 * Le GET est la SEULE méthode à être rejouée automatiquement, et une seule
 * fois : il est idempotent — le refaire ne peut ni créer de doublon ni modifier
 * deux fois la même donnée. Rejouer un POST ou un DELETE dont la réponse
 * s'est perdue pourrait exécuter l'action DEUX fois ; là, il faut que l'échec
 * remonte et que l'utilisateur décide.
 *
 * Conditions, chacune pour une raison :
 *
 *   * `isNetwork` ou 5xx — fautes qui peuvent être transitoires (microcoupure,
 *     redémarrage). Un 4xx est une réponse DÉFINITIVE : la rejouer rendrait le
 *     même verdict ;
 *   * signal non annulé : l'annulation d'une requête par `useAsyncRequest`
 *     atterrit ici déguisée en erreur réseau (pas de réponse) — la rejouer
 *     partirait contre la volonté de l'appelant ;
 *   * pas de rafraîchissement : `/auth/refresh` est un POST nu hors de
 *     l'instance `http`, donc immunisé par construction.
 */
function doitRejouerGet(erreur: unknown, config?: AxiosRequestConfig): boolean {
  if (config?.signal?.aborted) return false
  if (!(erreur instanceof ApiError)) return false
  return erreur.isNetwork || erreur.status >= 500
}

export async function post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.post<T>(url, body, config)
  return data
}

export async function put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.put<T>(url, body, config)
  return data
}

export async function patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.patch<T>(url, body, config)
  return data
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.delete<T>(url, config)
  return data
}
